import Phaser from 'phaser';
import { findPath, adjacentWalkable } from '../world/pathfinding';
import { faceMove, faceIdle, type Facing } from '../world/playerFacing';
import { TX, AUDIO } from '../registry/keys';
import { CONFIG } from '../data/config';
import { playSfx } from '../audio/sfx';
import { store } from '../state/store';
import { getItem } from '../data/items';
import { tileKey, type TileXY } from '../types';
import { getHat, hatTexture } from '../data/hats';
import type { UIScene } from './UIScene';

const TS = CONFIG.tileSize;
const MW = 11;
const MH = 8;
const MAX_DEPTH = 12; // the bottom of the mine
const EXIT: TileXY = { x: 5, y: 7 }; // ladder back up to the house
const DEEPER: TileXY = { x: 9, y: 1 }; // ladder down to a richer level
const ENTRY: TileXY = { x: 5, y: 6 };
const ORIGIN_Y = 0.7;

/** Ore rocks (flags 'foragem*', refresh each morning with the forest/beach). */
const ROCKS: TileXY[] = [
  { x: 2, y: 2 }, { x: 8, y: 2 }, { x: 3, y: 4 }, { x: 7, y: 4 }, { x: 5, y: 2 }, { x: 5, y: 5 },
];
const WALLS: TileXY[] = [
  { x: 2, y: 5 }, { x: 8, y: 5 }, { x: 4, y: 3 }, { x: 6, y: 3 },
];

/** The mine: tap ore rocks for stone/coal/ore/gems, ladder back up to the house. */
export class MineScene extends Phaser.Scene {
  private player!: Phaser.GameObjects.Sprite;
  private blocked = new Set<string>();
  private path: TileXY[] = [];
  private pendingArrival: (() => void) | null = null;
  private playerTile: TileXY = { ...ENTRY };
  private leaving = false;
  private hat?: Phaser.GameObjects.Image;
  private lastFacing: Facing = 'down';
  private spotSprites = new Map<number, Phaser.GameObjects.Image>();
  private depth = 1;

  constructor() {
    super('Mine');
  }

  init(data: { depth?: number }): void {
    this.depth = data.depth ?? 1;
  }

  private get ui(): UIScene {
    return this.scene.get('UI') as UIScene;
  }

  create(): void {
    this.leaving = false;
    this.blocked.clear();
    this.path = [];
    this.spotSprites.clear();
    this.cameras.main.setBackgroundColor('#16141c');
    const short = Math.min(this.scale.width, this.scale.height);
    this.cameras.main.setZoom(Phaser.Math.Clamp(Math.round(short / (MW * TS)), CONFIG.minZoom, CONFIG.maxZoom));
    this.cameras.main.centerOn((MW * TS) / 2, (MH * TS) / 2);

    this.buildMineMap();

    this.player = this.add.sprite(ENTRY.x * TS + TS / 2, ENTRY.y * TS + TS, TX.player, 0).setOrigin(0.5, ORIGIN_Y);
    this.player.setDepth(ENTRY.y * TS + TS);
    this.playerTile = { ...ENTRY };
    this.applyHat();

    this.add
      .text(this.scale.width / 2, 8, `Mine · depth ${this.depth} — ⬇ deeper · ⬆ home`, {
        fontFamily: 'monospace',
        fontSize: '11px',
        color: '#bcb4c8',
      })
      .setOrigin(0.5, 0)
      .setScrollFactor(0)
      .setResolution(2)
      .setDepth(1000);

    this.input.on('pointerdown', this.onTap, this);
    this.time.addEvent({
      delay: 6000,
      loop: true,
      callback: () => {
        if (Math.random() < 0.5) playSfx(this, AUDIO.drip);
      },
    });
    if (this.depth > 1) this.time.delayedCall(120, () => this.ui.showToast(`You reach depth ${this.depth} ⛏️`, 'info'));
  }

  private addRock(x: number, y: number): void {
    this.add.image(x * TS, y * TS, TX.mineRock).setOrigin(0, 0).setDepth(y * TS + TS).setTint(this.depthTint(1.15));
    this.blocked.add(tileKey(x, y));
  }

  /** A colour theme per depth band (stone → copper → ice → ember), darkening as you descend. */
  private depthTint(mult = 1): number {
    const bands = [
      [0xb4, 0xb4, 0xc4], // stone — grey-blue
      [0xc6, 0xa2, 0x76], // copper cavern — warm brown
      [0x86, 0xb2, 0xca], // frozen depths — icy blue
      [0xcc, 0x86, 0x80], // the deep — ember red
    ];
    const band = Math.min(bands.length - 1, Math.floor((this.depth - 1) / 3));
    const c = bands[band]!;
    const dark = Math.max(0.45, 1 - ((this.depth - 1) % 3) * 0.13 - band * 0.05) * mult;
    const ch = (v: number): number => Math.min(255, Math.round(v * dark));
    return (ch(c[0]!) << 16) | (ch(c[1]!) << 8) | ch(c[2]!);
  }

  private buildMineMap(): void {
    const floorTint = this.depthTint(); // each depth band gets its own colour theme
    for (let y = 0; y < MH; y++) {
      for (let x = 0; x < MW; x++) {
        this.add.image(x * TS, y * TS, TX.mineFloor).setOrigin(0, 0).setDepth(0).setTint(floorTint);
      }
    }
    for (let y = 0; y < MH; y++) {
      for (let x = 0; x < MW; x++) {
        const border = x === 0 || x === MW - 1 || y === 0 || y === MH - 1;
        if (border && !(x === EXIT.x && y === EXIT.y)) this.addRock(x, y);
      }
    }
    this.add.image(EXIT.x * TS, EXIT.y * TS, TX.ladder).setOrigin(0, 0).setDepth(1); // ladder up to the house
    if (this.depth < MAX_DEPTH) this.add.image(DEEPER.x * TS, DEEPER.y * TS, TX.ladder).setOrigin(0, 0).setDepth(1).setTint(0x9a8aaa); // ladder down
    const tag = (tx: number, ty: number, s: string, col: string): void => {
      this.add
        .text(tx * TS + TS / 2, ty * TS - 3, s, { fontFamily: 'monospace', fontSize: '8px', color: col, backgroundColor: '#000000aa', padding: { x: 2, y: 1 } })
        .setOrigin(0.5, 1)
        .setDepth(60)
        .setResolution(2);
    };
    tag(EXIT.x, EXIT.y, '↑ Home', '#ffe9a8');
    tag(DEEPER.x, DEEPER.y, this.depth < MAX_DEPTH ? '↓ Deeper' : '⛏ Bottom', this.depth < MAX_DEPTH ? '#c8b0e0' : '#a89878');
    for (const wRock of WALLS) this.addRock(wRock.x, wRock.y);

    ROCKS.forEach((s, i) => {
      const mined = !!store.get().flags['foragem' + this.depth + '_' + i];
      const img = this.add
        .image(s.x * TS + TS / 2, s.y * TS + TS, TX.oreRock)
        .setOrigin(0.5, 1)
        .setDepth(s.y * TS + TS)
        .setAlpha(mined ? 0.3 : 1);
      this.spotSprites.set(i, img);
      this.blocked.add(tileKey(s.x, s.y));
    });
  }

  private isBlocked = (x: number, y: number): boolean =>
    x < 0 || y < 0 || x >= MW || y >= MH || this.blocked.has(tileKey(x, y));

  private onTap(pointer: Phaser.Input.Pointer): void {
    if (this.leaving || this.ui.isModalOpen() || this.ui.consumesPointer(pointer.x, pointer.y)) return;
    const tile = { x: Math.floor(pointer.worldX / TS), y: Math.floor(pointer.worldY / TS) };
    if (tile.x < 0 || tile.y < 0 || tile.x >= MW || tile.y >= MH) return;

    const idx = ROCKS.findIndex((s) => s.x === tile.x && s.y === tile.y);
    if (idx >= 0) return this.routeToInteractable(tile, () => this.mine(idx));
    if (tile.x === EXIT.x && tile.y === EXIT.y) return this.routeTo(tile, () => this.leave());
    if (tile.x === DEEPER.x && tile.y === DEEPER.y) return this.routeTo(tile, () => this.goDeeper());
    if (!this.isBlocked(tile.x, tile.y)) this.routeTo(tile, null);
  }

  private actionBob(): void {
    this.player.setScale(1, 1);
    this.tweens.add({ targets: this.player, scaleX: 1.12, scaleY: 0.85, duration: 95, yoyo: true, ease: 'Quad.easeOut', onComplete: () => this.player.setScale(1, 1) });
  }

  /** Swing the pickaxe (real action frames), then restore the walk sheet. */
  private playerSwing(): void {
    if (!this.anims.exists('act-swing')) return;
    this.player.setTexture(TX.playerAction);
    this.player.play('act-swing');
    this.player.once(Phaser.Animations.Events.ANIMATION_COMPLETE, () => {
      this.player.setTexture(TX.player);
      this.player.setFrame(0);
    });
  }

  private goDeeper(): void {
    if (this.leaving) return;
    if (this.depth >= MAX_DEPTH) {
      this.ui.showToast("You've reached the bottom of the mine ⛏", 'info');
      return;
    }
    this.leaving = true;
    playSfx(this, AUDIO.pop);
    this.scene.restart({ depth: this.depth + 1 });
  }

  private burst(x: number, y: number, tex: string, n: number): void {
    const p = this.add
      .particles(x, y, tex, { speed: { min: 20, max: 70 }, angle: { min: 200, max: 340 }, scale: { start: 1.3, end: 0 }, lifespan: 420, quantity: n, emitting: false })
      .setDepth(9000);
    p.explode(n);
    this.time.delayedCall(550, () => p.destroy());
  }

  private mine(idx: number): void {
    const fkey = 'foragem' + this.depth + '_' + idx;
    if (store.get().flags[fkey]) {
      this.ui.showToast('Already mined this today', 'info');
      return;
    }
    if (!store.trySpendEnergy('mine')) {
      this.ui.showToast('Too tired — rest up', 'warn');
      return;
    }
    this.actionBob();
    this.playerSwing();
    store.update((s) => (s.flags[fkey] = true));
    // Weighted table: every depth stays varied; deeper shifts toward copper/iron/gems (gems capped, never the whole table).
    const d = this.depth;
    const weights: [string, number][] = [
      ['stone', Math.max(8, 34 - d * 2)],
      ['coal', 22],
      ['copper_ore', 18 + d],
      ['iron_ore', 9 + d * 1.6],
      ['gem', Math.min(18, 2 + d * 1.3)],
    ];
    let r = Math.random() * weights.reduce((s, w) => s + w[1], 0);
    let item = 'stone';
    for (const [id, wt] of weights) {
      if (r < wt) {
        item = id;
        break;
      }
      r -= wt;
    }
    store.addItem(item, 1);
    playSfx(this, AUDIO.chop);
    this.spotSprites.get(idx)?.setAlpha(0.3);
    if (item === 'gem') {
      const spot = ROCKS[idx]!;
      this.burst(spot.x * TS + TS / 2, spot.y * TS + TS / 2, TX.sparkle, 12);
      this.cameras.main.shake(160, 0.005);
      store.logJournal('Dug up a sparkling gem 💎');
    }
    this.ui.showToast(`+1 ${getItem(item)?.name ?? item}`, item === 'gem' ? 'good' : 'info');
  }

  private routeTo(tile: TileXY, action: (() => void) | null): void {
    if (this.playerTile.x === tile.x && this.playerTile.y === tile.y) {
      action?.();
      return;
    }
    const path = findPath(this.playerTile, tile, MW, MH, this.isBlocked);
    if (!path) return;
    this.path = path;
    this.pendingArrival = action;
  }

  private routeToInteractable(target: TileXY, action: () => void): void {
    if (Math.abs(this.playerTile.x - target.x) + Math.abs(this.playerTile.y - target.y) <= 1) {
      action();
      return;
    }
    const adj = adjacentWalkable(target, this.playerTile, MW, MH, this.isBlocked);
    if (!adj) return;
    const path = findPath(this.playerTile, adj, MW, MH, this.isBlocked);
    if (!path) return;
    this.path = path;
    this.pendingArrival = action;
  }

  private leave(): void {
    if (this.leaving) return;
    this.leaving = true;
    playSfx(this, AUDIO.pop);
    this.scene.stop();
    this.scene.resume('Interior');
  }

  applyHat(): void {
    if (this.hat) {
      this.hat.destroy();
      this.hat = undefined;
    }
    const tex = hatTexture(getHat());
    if (tex) {
      this.hat = this.add.image(this.player.x, this.player.y - 16, tex).setOrigin(0.5, 0.5).setDepth(this.player.depth + 1);
    }
  }

  update(_time: number, delta: number): void {
    if (this.hat) {
      this.hat.setPosition(this.player.x, this.player.y - 16).setFlipX(this.player.flipX).setDepth(this.player.depth + 1);
    }
    if (this.path.length === 0) return;
    const target = this.path[0]!;
    const fx = target.x * TS + TS / 2;
    const fy = target.y * TS + TS;
    const dx = fx - this.player.x;
    const dy = fy - this.player.y;
    const dist = Math.hypot(dx, dy);
    this.lastFacing = faceMove(this, this.player, dx, dy);
    const step = (CONFIG.walkTilesPerSecond * TS * delta) / 1000;
    if (dist <= step || dist === 0) {
      this.player.setPosition(fx, fy);
      this.player.setDepth(fy);
      this.playerTile = { x: target.x, y: target.y };
      this.path.shift();
      if (this.path.length === 0) {
        faceIdle(this, this.player, this.lastFacing);
        const a = this.pendingArrival;
        this.pendingArrival = null;
        a?.();
      }
    } else {
      this.player.x += (dx / dist) * step;
      this.player.y += (dy / dist) * step;
      this.player.setDepth(this.player.y);
    }
  }
}
